import { useEffect, useMemo, useState } from 'react';
import { Select, Spin, Empty, Button, Tag, Tabs, Modal, Input, message } from 'antd';
import { territoryApi } from '../../features/territory/territoryApi';
import {
  useGetSyncStatusQuery,
  useRunSyncMutation,
  useGetSyncJobsQuery,
  useErollStartMutation,
  useErollSubmitMutation,
  useErollStatusMutation,
  useGetStatePcsQuery,
  useSavePcNamesBulkMutation,
  useGetSecDistrictsQuery,
  useGetSecBodiesQuery,
} from '../../features/dataSync/dataSyncApi';
import type { PcRow } from '../../features/dataSync/dataSyncApi';

const STATUS_COLOR: Record<string, string> = {
  queued: 'default',
  running: 'processing',
  done: 'success',
  failed: 'error',
};

// show the same friendly names as the step buttons, not the raw script kind
const KIND_LABELS: Record<string, string> = {
  state: 'States',
  district: 'Districts',
  ac: 'Parliaments + Assemblies',
  stations: 'Polling Stations',
  download: 'Download e-roll',
  convert: 'Convert to images',
  ocr_first: 'OCR first pages',
  ocr_voter: 'OCR voter pages',
  ward_download: 'Download ward rolls',
  ward_match: 'Match ward ↔ booth',
};

const fmtWhen = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN');
};

const CATEGORY_OPTS = [
  { value: 'GEN', label: 'GEN' },
  { value: 'SC', label: 'SC' },
  { value: 'ST', label: 'ST' },
];

/**
 * Parliament-name editor. ECI's API only gives the PC name in the regional
 * script, so this lists a state's parliaments with an editable English-name
 * input (pre-filled with a transliteration guess) + category. Edit any rows,
 * then hit ONE "Save changes" button (saves only the rows you touched).
 */
function PcNameEditor({ stateCode, stateName }: { stateCode: string; stateName?: string }) {
  const { data: pcs = [], isFetching } = useGetStatePcsQuery(
    { state: stateCode }, { skip: !stateCode });
  const [saveBulk, { isLoading: saving }] = useSavePcNamesBulkMutation();
  // per-row draft { pc_code: {name, category} } — only rows the user touched
  const [edits, setEdits] = useState<Record<string, { name: string; category: string }>>({});

  // reset drafts when the state changes
  useEffect(() => setEdits({}), [stateCode]);

  const savedFor = (p: PcRow) => ({
    name: p.is_local ? '' : p.name || '', // a local-script name counts as "not yet set"
    category: p.category || '',
  });
  const draftFor = (p: PcRow) => edits[p.pc_code] ?? savedFor(p);

  const setDraft = (p: PcRow, patch: Partial<{ name: string; category: string }>) =>
    setEdits((e) => ({ ...e, [p.pc_code]: { ...draftFor(p), ...patch } }));

  const isDirty = (p: PcRow) => {
    const d = edits[p.pc_code];
    if (!d) return false;
    const s = savedFor(p);
    return d.name !== s.name || d.category !== s.category;
  };

  const dirtyRows = pcs.filter(isDirty);
  const needsFixing = pcs.filter((p) => p.is_local && !isDirty(p)).length;

  // pre-fill every still-local row with its transliteration guess in one click
  const fillAllGuesses = () =>
    setEdits((e) => {
      const next = { ...e };
      pcs.forEach((p) => {
        if (p.is_local && !next[p.pc_code]) {
          next[p.pc_code] = { name: p.suggested || '', category: p.category || '' };
        }
      });
      return next;
    });

  const saveAll = async () => {
    const items = dirtyRows
      .map((p) => ({ pc_code: p.pc_code, name: draftFor(p).name.trim(), category: draftFor(p).category || undefined }))
      .filter((i) => i.name);
    if (!items.length) {
      message.info('Nothing to save yet.');
      return;
    }
    try {
      const r = await saveBulk({ items }).unwrap();
      message.success(`Saved ${r.saved_count} name${r.saved_count === 1 ? '' : 's'}.`);
      setEdits({});
    } catch (e: any) {
      message.error(e?.data?.detail || 'Could not save.');
    }
  };

  if (!stateCode) return null;

  return (
    <div className="rounded-xl bg-white p-6 shadow-card">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-[#00173e]">
          Parliament names {stateName ? `· ${stateName}` : ''}
        </h2>
        {pcs.length > 0 && (
          <div className="flex items-center gap-2">
            {needsFixing > 0 && (
              <Button size="small" onClick={fillAllGuesses}>Fill all guesses</Button>
            )}
            <Button
              type="primary"
              loading={saving}
              disabled={dirtyRows.length === 0}
              onClick={saveAll}
            >
              Save changes{dirtyRows.length ? ` (${dirtyRows.length})` : ''}
            </Button>
          </div>
        )}
      </div>
      <p className="mb-4 text-sm text-ink-secondary">
        ECI only gives parliament names in the local language. Correct them to
        English (the box is pre-filled with a rough guess), then hit <b>Save
        changes</b> once — only edited rows are saved.
        {needsFixing > 0 && (
          <span className="ml-1 text-amber-600">{needsFixing} still need fixing.</span>
        )}
      </p>
      {isFetching ? (
        <Spin />
      ) : pcs.length === 0 ? (
        <Empty description="No parliaments yet — run 'Parliaments + Assemblies' first." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#eef4ff] text-left text-xs uppercase tracking-wide text-[#64748b]">
                <th className="py-2 pr-3 w-10">#</th>
                <th className="py-2 pr-3">Local name</th>
                <th className="py-2 pr-3">English name</th>
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3 w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {pcs.map((p) => {
                const d = draftFor(p);
                const dirty = isDirty(p);
                const done = !p.is_local || (d.name.trim() && !dirty);
                return (
                  <tr
                    key={p.pc_code}
                    className={['border-b border-[#f5f8ff]', dirty ? 'bg-[#fffbeb]' : ''].join(' ')}
                  >
                    <td className="py-2 pr-3 text-xs text-ink-secondary">{p.pc_no ?? '—'}</td>
                    <td className="py-2 pr-3">
                      {p.is_local
                        ? <span className="text-[#334155]">{p.name}</span>
                        : <span className="text-ink-secondary">—</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <Input
                        size="small"
                        value={d.name}
                        placeholder="Type English name"
                        status={p.is_local && !d.name.trim() ? 'warning' : undefined}
                        onChange={(e) => setDraft(p, { name: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <Select
                        size="small"
                        style={{ width: 90 }}
                        allowClear
                        placeholder="—"
                        value={d.category || undefined}
                        options={CATEGORY_OPTS}
                        onChange={(v) => setDraft(p, { category: v || '' })}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      {dirty ? (
                        <Tag color="warning">unsaved</Tag>
                      ) : done ? (
                        <Tag color="success">ok</Tag>
                      ) : (
                        <Tag color="default">needs name</Tag>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * ECI Data Pull (system admin only; access enforced by backend `IsSystemAdmin`).
 * Pick State → Parliament → Assembly, see what's already synced, and run the
 * hierarchy pull scripts (state / district / assembly / polling stations). The
 * voter-roll OCR pipeline stays manual (it needs a human captcha).
 */
export default function DataPullPage() {
  const [stateCode, setStateCode] = useState<string>('');   // state_cd (e.g. S20)
  const [stateId, setStateId] = useState<number | ''>('');
  const [pcCode, setPcCode] = useState<string>('');         // pc_code (S20_08)
  const [pcId, setPcId] = useState<number | ''>('');
  const [acCode, setAcCode] = useState<string>('');         // ac_code (S20_08_065)

  // dropdown data (territory APIs)
  const { data: states = [] } = territoryApi.useGetStatesQuery();
  const { data: pcs = [] } = territoryApi.useGetParliamentsQuery(
    { state: stateId as number }, { skip: stateId === '' });
  const { data: acs = [] } = territoryApi.useGetAssembliesQuery(
    { pc: pcId as number }, { skip: pcId === '' });

  // status for the DEEPEST selected level
  const scope = useMemo(() => {
    if (acCode) return { level: 'assembly', code: acCode };
    if (pcCode) return { level: 'parliament', code: pcCode };
    if (stateCode) return { level: 'state', code: stateCode };
    return undefined;
  }, [stateCode, pcCode, acCode]);

  const { data: status, isFetching } = useGetSyncStatusQuery(scope, { skip: !scope });

  // jobs list — poll while anything is queued/running so progress updates live
  // poll fast (3s) only while something is running; slow to 30s when idle
  const [jobsPollMs, setJobsPollMs] = useState(3000);
  const { data: jobs = [] } = useGetSyncJobsQuery(undefined, { pollingInterval: jobsPollMs });
  const [runSync, { isLoading: running }] = useRunSyncMutation();

  const busy = jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => { setJobsPollMs(busy ? 3000 : 30000); }, [busy]);
  const [jobsLimit, setJobsLimit] = useState(2);   // recent-jobs list: show 2, load more

  // when was this step last completed? (jobs come newest-first from the API)
  const lastSyncOf = (kind: string, code?: string) => {
    const j = jobs.find(
      (x) => x.kind === kind && x.status === 'done' && (code ? x.code === code : true));
    return j ? j.updated_at || j.created_at : null;
  };

  // the LATEST job for a step (any status) — first match since jobs are newest-first
  const jobOf = (kind: string, code?: string) =>
    jobs.find((x) => x.kind === kind && (code ? x.code === code : true));
  const isRunningJob = (kind: string, code?: string) => {
    const j = jobOf(kind, code);
    return !!j && (j.status === 'running' || j.status === 'queued');
  };

  const launch = async (kind: string, code?: string, label?: string) => {
    try {
      await runSync({ kind, code }).unwrap();
      message.success(`Started: ${label || kind}${code ? ` (${code})` : ''}`);
    } catch (e: any) {
      message.error(e?.data?.detail || 'Could not start the sync.');
    }
  };

  // ---- Ward rolls (SEC): District -> Body dropdowns (steps 6-7) ----
  const [secDistrict, setSecDistrict] = useState<string>('');
  const [secBody, setSecBody] = useState<string>('');
  const { data: secDistricts = [] } = useGetSecDistrictsQuery();
  const { data: secBodies = [] } = useGetSecBodiesQuery(
    { district: secDistrict }, { skip: !secDistrict });

  // ---- Step 1: ECINET e-roll picker RELAY (new portal, real browser) ----
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const EC_KEYS = ['state', 'year', 'roll', 'district', 'ac', 'language'] as const;
  type EcKey = typeof EC_KEYS[number];
  const [ecSel, setEcSel] = useState<Record<EcKey, string>>({
    state: '', year: '2026', roll: '', district: '', ac: '', language: '',
  });
  const [ecOptions, setEcOptions] = useState<Record<string, string[]>>({});
  const [captchaText, setCaptchaText] = useState('');
  const [session, setSession] = useState<{ session_id: string; captcha_image: string } | null>(null);
  const [erollStart, { isLoading: loadingCaptcha }] = useErollStartMutation();
  const [erollSubmit, { isLoading: submitting }] = useErollSubmitMutation();
  const [erollStatus] = useErollStatusMutation();
  const [downloading, setDownloading] = useState(false);

  const [ecSessionId, setEcSessionId] = useState<string>('');

  // one cascade step: apply selections -> refresh option lists.
  // wantCaptcha=true also fetches the captcha (only when the user is ready).
  const ecStep = async (sel: Record<EcKey, string>, wantCaptcha = false) => {
    try {
      const r = await erollStart({
        session_id: ecSessionId || undefined, ...sel, want_captcha: wantCaptcha,
      }).unwrap();
      setEcSessionId(r.session_id);
      if (r.options) setEcOptions(r.options);
      if (wantCaptcha && r.captcha_image) {
        setSession({ session_id: r.session_id, captcha_image: r.captcha_image });
      }
      return r.session_id;
    } catch (e: any) {
      message.error(e?.data?.detail || 'Could not reach ECINET.');
    }
  };

  const openCaptcha = () => {
    const start: Record<EcKey, string> = {
      state: stateName || '', year: '2026', roll: '', district: '', ac: '', language: '',
    };
    setEcSel(start);
    setEcOptions({});
    setEcSessionId('');
    setSession(null);
    setCaptchaText('');
    setCaptchaOpen(true);
    if (stateName) ecStep(start);   // load ECINET + initial option lists (no captcha yet)
  };

  // which dropdowns' lists change when you pick a given one (only these need a
  // server round-trip). Year/Roll/Language have no dependents -> instant, no load.
  const EC_DEPENDENTS: Record<EcKey, EcKey[]> = {
    state: ['roll', 'district', 'ac', 'language'],
    district: ['ac', 'language'],
    ac: ['language'],
    year: [], roll: [], language: [],
  };

  const changeEc = (key: EcKey, value: string) => {
    const next = { ...ecSel, [key]: value } as Record<EcKey, string>;
    EC_DEPENDENTS[key].forEach((k) => { next[k] = ''; });   // clear stale dependents
    setEcSel(next);
    setSession(null);        // selections changed -> old captcha is stale
    setCaptchaText('');
    // only hit ECINET when the change actually refreshes other lists
    if (EC_DEPENDENTS[key].length) ecStep(next);
  };

  const submitCaptcha = async () => {
    if (!session) return;
    const sid = session.session_id;
    try {
      await erollSubmit({ session_id: sid, captcha: captchaText }).unwrap();
    } catch (e: any) {
      message.error(e?.data?.detail || 'Failed — reload the captcha and retry.');
      return;
    }
    // download runs in the background now — poll for the result (no timeout)
    setDownloading(true);
    message.info('Download started on the server… this can take a while.');
    const poll = async () => {
      let s;
      try {
        s = await erollStatus({ session_id: sid }).unwrap();
      } catch {
        setTimeout(poll, 4000); return;
      }
      if (s.status === 'running') { setTimeout(poll, 4000); return; }
      setDownloading(false);
      const n = s.result?.count ?? s.result?.downloaded?.length ?? 0;
      if (s.status === 'done' && n) {
        message.success(`Downloaded ${n} file(s) on the server.`);
        setCaptchaOpen(false);
      } else if (s.status === 'done') {
        message.warning(s.result?.note || 'Finished, but no files — check the captcha / parts.');
      } else {
        message.error(s.result?.error || 'Download failed — reload the captcha and retry.');
      }
    };
    setTimeout(poll, 4000);
  };

  const opt = <T,>(arr: T[], val: (x: T) => string | number, lbl: (x: T) => string) =>
    arr.map((x) => ({ value: val(x), label: lbl(x) }));

  // resolved names for the "uses" hints
  const stateName = states.find((s) => (s.state_cd || s.state_code) === stateCode)?.name;
  const pcName = pcs.find((p) => p.pc_code === pcCode)?.name;

  // Ordered hierarchy steps. `need` tells the user exactly which dropdown to pick.
  const HIER_STEPS: {
    kind: string;
    title: string;
    desc: string;
    need: 'none' | 'state' | 'pc';
  }[] = [
    { kind: 'state', title: 'States', desc: 'Pulls the full list of states. No selection needed.', need: 'none' },
    { kind: 'district', title: 'Districts', desc: 'Pulls all districts of the chosen state.', need: 'state' },
    { kind: 'ac', title: 'Parliaments + Assemblies', desc: 'One pull creates both the parliamentary (PC) and assembly (AC) constituencies of the state.', need: 'state' },
    { kind: 'stations', title: 'Polling Stations', desc: 'Pulls the polling stations (booths) of the chosen parliament.', need: 'pc' },
  ];

  const needInfo = (need: 'none' | 'state' | 'pc') => {
    if (need === 'none') return { ok: true, code: undefined as string | undefined, label: 'No selection needed' };
    if (need === 'state')
      return { ok: !!stateCode, code: stateCode, label: stateCode ? `State: ${stateName || stateCode}` : 'Select a State above' };
    return { ok: !!pcCode, code: pcCode, label: pcCode ? `Parliament: ${pcName || pcCode}` : 'Select a Parliament above' };
  };

  // ---- Tab 1: hierarchy status + pull steps ----
  const hierarchyTab = (
    <div className="flex flex-col gap-6">
      {/* Status */}
      <div className="rounded-xl bg-white p-6 shadow-card">
        {!scope ? (
          <Empty description="Pick a State (and optionally Parliament / Assembly) to see the sync status." />
        ) : isFetching ? (
          <Spin />
        ) : status ? (
          <>
            <div className="mb-4 flex items-center gap-2">
              <span
                className={[
                  'rounded-full px-3 py-1 text-xs font-semibold',
                  status.synced ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700',
                ].join(' ')}
              >
                {status.synced ? 'Already synced' : 'Not synced yet'}
              </span>
              <span className="text-sm text-ink-secondary">
                {status.level.toUpperCase()} {status.code ? `· ${status.code}` : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {Object.entries(status.counts).map(([k, v]) => (
                <div key={k} className="rounded-lg border border-[#eef4ff] bg-[#f8fafc] p-4">
                  <div className="text-2xl font-semibold text-[#00173e]">
                    {Number(v).toLocaleString('en-IN')}
                  </div>
                  <div className="text-xs uppercase tracking-wide text-[#64748b]">
                    {k.replace(/_/g, ' ')}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <Empty description="No status." />
        )}
      </div>

      {/* Run steps — ordered, each shows which dropdown it uses */}
      <div className="rounded-xl bg-white p-6 shadow-card">
        <h2 className="mb-1 text-lg font-semibold text-[#00173e]">Sync the hierarchy</h2>
        <p className="mb-4 text-sm text-ink-secondary">
          Run these top-to-bottom. Each step tells you which dropdown above it uses.
        </p>
        <div className="flex flex-col divide-y divide-[#eef4ff]">
          {HIER_STEPS.map((step, i) => {
            const info = needInfo(step.need);
            return (
              <div key={step.kind} className="flex items-center gap-4 py-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-shade text-sm font-semibold text-brand">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#00173e]">{step.title}</div>
                  <div className="text-xs text-ink-secondary">{step.desc}</div>
                  <div className={info.ok ? 'mt-1 text-xs text-green-700' : 'mt-1 text-xs text-amber-600'}>
                    Uses → {info.label}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Button
                    type={step.kind === 'stations' ? 'primary' : 'default'}
                    onClick={() => launch(step.kind, info.code, step.title)}
                    loading={running}
                    disabled={busy || !info.ok}
                  >
                    Sync
                  </Button>
                  {(() => {
                    // only meaningful once the required selection is made
                    if (step.need !== 'none' && !info.ok) return null;
                    const when = lastSyncOf(step.kind, step.need === 'none' ? undefined : info.code);
                    return when ? (
                      <span className="text-xs text-green-600 whitespace-nowrap">
                        ✓ Last synced {fmtWhen(when)}
                      </span>
                    ) : (
                      <span className="text-xs text-red-500 whitespace-nowrap">Not synced</span>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
        {busy && (
          <p className="mt-3 text-xs text-amber-600">
            A job is running — wait for it to finish before starting another.
          </p>
        )}
      </div>

      {/* Fix parliament names (ECI gives them only in the local language) */}
      {stateCode && <PcNameEditor stateCode={stateCode} stateName={stateName} />}
    </div>
  );

  // OCR pipeline (all need an assembly). Step 1 downloads every part straight
  // from ECINET's public URL — no captcha, no browser, no 10-part limit.
  const OCR_STEPS: { kind: string; title: string; desc: string; danger?: boolean }[] = [
    { kind: 'eroll_direct', title: 'Download e-roll (all parts)', desc: 'Downloads every booth PDF directly from ECINET — no captcha, no limit.' },
    { kind: 'convert', title: 'Convert to images', desc: 'Converts the PDFs into page images.' },
    { kind: 'ocr_first', title: 'OCR first pages', desc: 'Reads booth / section headers. Paid (Google Vision).' },
    { kind: 'ocr_voter', title: 'OCR voter pages', desc: 'Reads voter details — fills the voter table. Paid (Google Vision).', danger: true },
  ];

  // ---- Tab 2: voter-roll OCR pipeline ----
  const ocrTab = (
    <div className="rounded-xl bg-white p-6 shadow-card">
      <h2 className="mb-1 text-lg font-semibold text-[#00173e]">
        Voter Rolls (OCR)
        {acCode && <span className="ml-2 text-sm font-normal text-ink-secondary">· {acCode}</span>}
      </h2>
      <p className="mb-4 text-sm text-ink-secondary">
        Run these top-to-bottom for one assembly. The last step fills the voter
        table. Heavy — one assembly is ~15&nbsp;GB of downloads plus paid Google
        Vision OCR.
      </p>

      <div className="flex flex-col divide-y divide-[#eef4ff]">
        {/* Steps 1–4 run serially — each unlocks the next */}
        {(() => {
          const done = OCR_STEPS.map((s) => (acCode ? !!lastSyncOf(s.kind, acCode) : false));
          const nextIdx = done.findIndex((d) => !d);   // first step not yet done
          return OCR_STEPS.map((step, i) => {
            const isDone = done[i];
            const inProgress = !!acCode && isRunningJob(step.kind, acCode);
            const isNext = !!acCode && i === nextIdx;
            const locked = !!acCode && nextIdx >= 0 && i > nextIdx;
            return (
              <div
                key={step.kind}
                className={['flex items-center gap-4 py-3 px-2 -mx-2 rounded',
                  isNext ? 'bg-[#eff6ff]' : ''].join(' ')}
              >
                <span className={['flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                  isDone ? 'bg-green-100 text-green-700'
                    : isNext ? 'bg-brand text-white' : 'bg-brand-shade text-brand'].join(' ')}>
                  {isDone ? '✓' : i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#00173e]">
                    {step.title}
                    {isNext && <span className="ml-2 text-xs font-normal text-brand">← do this next</span>}
                  </div>
                  <div className="text-xs text-ink-secondary">{step.desc}</div>
                  <div className={locked ? 'mt-1 text-xs text-ink-secondary'
                    : acCode ? 'mt-1 text-xs text-green-700' : 'mt-1 text-xs text-amber-600'}>
                    {locked ? `Finish step ${nextIdx + 1} first`
                      : acCode ? `Uses → Assembly: ${acCode}` : 'Select an Assembly above'}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Button
                    type={isNext || (step.danger && !locked) ? 'primary' : 'default'}
                    danger={step.danger && !isNext}
                    onClick={() => launch(step.kind, acCode, step.title)}
                    loading={running || inProgress}
                    disabled={busy || !acCode || locked}
                  >
                    {step.kind === 'eroll_direct' ? 'Fetch' : 'Sync'}
                  </Button>
                  {acCode && (inProgress ? (
                    <span className="text-xs text-blue-600 whitespace-nowrap">⏳ in progress…</span>
                  ) : isDone ? (
                    <span className="text-xs text-green-600 whitespace-nowrap">
                      ✓ {step.kind === 'eroll_direct' ? 'fetched' : 'done'} {fmtWhen(lastSyncOf(step.kind, acCode)!)}
                    </span>
                  ) : locked ? (
                    <span className="text-xs text-ink-secondary whitespace-nowrap">locked</span>
                  ) : (
                    <span className="text-xs text-red-500 whitespace-nowrap">not run yet</span>
                  ))}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Fallback: the old ECINET captcha relay (kept for future; direct download
          above replaces it). Only needed if the public-URL download ever breaks. */}
      <details className="mt-4 text-xs text-ink-secondary">
        <summary className="cursor-pointer">Fallback: captcha relay (if direct download fails)</summary>
        <div className="mt-2">
          <Button size="small" onClick={openCaptcha} disabled={busy || !acCode}>
            Fetch via captcha
          </Button>
        </div>
      </details>

      {/* Ward mapping (SEC) — steps 5-6, own District -> Body dropdowns */}
      <div className="mt-6 border-t border-[#eef4ff] pt-5">
        <h3 className="text-base font-semibold text-[#00173e]">Ward mapping (SEC)</h3>
        <p className="mb-3 text-sm text-ink-secondary">
          Connects <b>ward → booth → village</b> by matching EPIC ids. No captcha,
          no paid OCR. <b>Download (5)</b> is a separate source — run it anytime.
          <b> Match (6)</b> needs the assemblies OCR'd (step 4) first, so booths
          have their voters.
        </p>
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#64748b]">SEC District</span>
            <Select
              showSearch optionFilterProp="label" placeholder="Select district…"
              value={secDistrict || undefined}
              options={secDistricts.map((d) => ({ value: d.code, label: d.name }))}
              onChange={(v) => { setSecDistrict(v); setSecBody(''); }}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#64748b]">Body (Nagar Nigam / Palika / Panchayat Samiti)</span>
            <Select
              showSearch optionFilterProp="label" placeholder="Select body…"
              disabled={!secDistrict}
              value={secBody || undefined}
              options={secBodies.map((b) => ({ value: b.code, label: b.name }))}
              onChange={(v) => setSecBody(v)}
            />
          </label>
        </div>

        <div className="flex flex-col divide-y divide-[#eef4ff]">
          {/* Step 5 — download ward rolls (independent of the ECI OCR steps) */}
          <div className="flex items-center gap-4 py-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-shade text-sm font-semibold text-brand">5</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#00173e]">Download ward rolls</div>
              <div className="text-xs text-ink-secondary">
                Downloads every ward &amp; part PDF for this body and extracts EPIC ids.
              </div>
              <div className={secBody ? 'mt-1 text-xs text-green-700' : 'mt-1 text-xs text-amber-600'}>
                Uses → {secBody ? `Body: ${secBodies.find((b) => b.code === secBody)?.name || secBody}` : 'Select a Body above'}
              </div>
            </div>
            <Button
              onClick={() => launch('ward_download', `${secDistrict}:${secBody}`, 'Download ward rolls')}
              loading={running} disabled={busy || !secBody}
            >
              Sync
            </Button>
          </div>

          {/* Step 6 — match (needs step 4 done) */}
          <div className="flex items-center gap-4 py-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-shade text-sm font-semibold text-brand">6</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#00173e]">Match ward ↔ booth</div>
              <div className="text-xs text-ink-secondary">
                Matches the ward EPICs to the OCR'd booths → ward ↔ booth ↔ village.
              </div>
              <div className={secBody ? 'mt-1 text-xs text-green-700' : 'mt-1 text-xs text-amber-600'}>
                Uses → {secBody ? `Body: ${secBodies.find((b) => b.code === secBody)?.name || secBody}` : 'Select a Body above'}
              </div>
            </div>
            <Button
              type="primary"
              onClick={() => launch('ward_match', secBody, 'Match ward ↔ booth')}
              loading={running} disabled={busy || !secBody}
            >
              Match
            </Button>
          </div>
        </div>
      </div>

      {busy && (
        <p className="mt-3 text-xs text-amber-600">
          A job is running — wait for it to finish before starting another.
        </p>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl bg-white px-8 py-5 shadow-card">
        <h1 className="text-2xl font-semibold text-[#00173e]">Electoral Data</h1>
        <p className="text-sm text-ink-secondary">
          Pull the ECI hierarchy, OCR the voter rolls, and map wards — all in one place.
        </p>
      </div>

      {/* Cascading selectors */}
      <div className="grid grid-cols-1 gap-4 rounded-xl bg-white p-6 shadow-card md:grid-cols-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[#64748b]">State</span>
          <Select
            showSearch optionFilterProp="label" placeholder="Select state…"
            value={stateId === '' ? undefined : stateId}
            options={opt(states, (s) => s.id, (s) => s.name)}
            onChange={(id) => {
              const s = states.find((x) => x.id === id);
              setStateId(id); setStateCode(s?.state_cd || s?.state_code || '');
              setPcId(''); setPcCode(''); setAcCode('');
            }}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[#64748b]">Parliament</span>
          <Select
            showSearch optionFilterProp="label" placeholder="Select parliament…"
            disabled={stateId === ''}
            value={pcId === '' ? undefined : pcId}
            options={opt(pcs, (p) => p.id, (p) => p.name)}
            onChange={(id) => {
              const p = pcs.find((x) => x.id === id);
              setPcId(id); setPcCode(p?.pc_code || '');
              setAcCode('');
            }}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[#64748b]">Assembly</span>
          <Select
            showSearch optionFilterProp="label" placeholder="Select assembly…"
            disabled={pcId === ''}
            value={acCode || undefined}
            options={opt(acs, (a) => a.ac_code || '', (a) => a.name)}
            onChange={(code) => setAcCode(code)}
          />
        </label>
      </div>

      {/* Two tabs: hierarchy data vs voter-roll OCR */}
      <Tabs
        items={[
          { key: 'hierarchy', label: 'ECI Data Pull', children: hierarchyTab },
          { key: 'ocr', label: 'Voter Rolls (OCR)', children: ocrTab },
        ]}
      />

      {/* Recent jobs / progress */}
      <div className="rounded-xl bg-white p-6 shadow-card">
        <h2 className="mb-4 text-lg font-semibold text-[#00173e]">Recent jobs</h2>
        {jobs.length === 0 ? (
          <Empty description="No jobs run yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eef4ff] text-left text-xs uppercase tracking-wide text-[#64748b]">
                  <th className="py-2 pr-4">What</th>
                  <th className="py-2 pr-4">Target</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">By</th>
                  <th className="py-2 pr-4">Details</th>
                </tr>
              </thead>
              <tbody>
                {jobs.slice(0, jobsLimit).map((j) => (
                  <tr key={j.id} className="border-b border-[#f5f8ff] align-top">
                    <td className="py-2 pr-4 font-medium">{KIND_LABELS[j.kind] || j.kind}</td>
                    <td className="py-2 pr-4">
                      {j.code ? (
                        <>
                          <span className="font-mono text-xs">{j.code}</span>
                          {j.code_name && <span className="text-ink-secondary"> · {j.code_name}</span>}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <Tag color={STATUS_COLOR[j.status] || 'default'}>{j.status}</Tag>
                    </td>
                    <td className="py-2 pr-4 text-xs text-ink-secondary whitespace-nowrap">{fmtWhen(j.updated_at || j.created_at)}</td>
                    <td className="py-2 pr-4 text-xs text-ink-secondary">{j.created_by || '—'}</td>
                    <td className="py-2 pr-4">
                      {j.message ? (
                        <pre className="max-h-24 max-w-md overflow-auto whitespace-pre-wrap rounded bg-[#f8fafc] p-2 text-[11px] text-[#475569]">
                          {j.message}
                        </pre>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {jobs.length > jobsLimit ? (
              <button
                className="mt-3 text-xs font-medium text-brand hover:underline"
                onClick={() => setJobsLimit((n) => n + 10)}
              >
                Load more ({jobs.length - jobsLimit} older)
              </button>
            ) : jobsLimit > 2 ? (
              <button
                className="mt-3 text-xs font-medium text-ink-secondary hover:underline"
                onClick={() => setJobsLimit(2)}
              >
                Show less
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* Step 1 — ECINET e-roll captcha relay popup */}
      <Modal
        title={`Fetch e-roll (ECINET) · ${stateName || ''}`}
        open={captchaOpen}
        onCancel={() => setCaptchaOpen(false)}
        onOk={submitCaptcha}
        okText={downloading ? 'Downloading…' : 'Submit & Download'}
        okButtonProps={{ loading: submitting || downloading, disabled: !session || !captchaText }}
        confirmLoading={submitting || downloading}
        width={560}
      >
        <Spin spinning={loadingCaptcha} tip="Talking to ECINET…">
        <div className="flex flex-col gap-3 py-2">
          <p className="text-xs text-ink-secondary">
            Pick the roll below (options come live from ECINET), then type the
            captcha and Submit — ECINET downloads the e-roll PDFs.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {([
              ['state', 'State'], ['year', 'Year of revision'], ['roll', 'Roll'],
              ['district', 'District'], ['ac', 'AC (assembly)'], ['language', 'Language'],
            ] as [EcKey, string][]).map(([key, label]) => (
              <label key={key} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-[#64748b]">{label}</span>
                <Select
                  showSearch optionFilterProp="label" size="middle"
                  placeholder={`Select ${label.toLowerCase()}…`}
                  value={ecSel[key] || undefined}
                  options={(ecOptions[key] || []).map((o) => ({ value: o, label: o }))}
                  onChange={(v) => changeEc(key, v)}
                />
              </label>
            ))}
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#64748b]">Captcha</span>
            <div className="flex items-center gap-3">
              {session ? (
                <img
                  src={session.captcha_image}
                  alt="captcha"
                  className="h-[60px] rounded border border-[#eef4ff] bg-white"
                />
              ) : (
                <div className="flex h-[60px] w-[180px] items-center justify-center rounded border border-[#eef4ff] bg-[#f8fafc] text-xs text-ink-secondary">
                  {ecSessionId ? 'click Load captcha' : 'select a state'}
                </div>
              )}
              <Button
                size="small" type={session ? 'default' : 'primary'}
                onClick={() => ecStep(ecSel, true)}
                disabled={!ecSessionId}
              >
                {session ? 'Reload' : 'Load captcha'}
              </Button>
            </div>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#64748b]">Type the captcha above</span>
            <Input
              value={captchaText}
              disabled={!session}
              onChange={(e) => setCaptchaText(e.target.value)}
              onPressEnter={submitCaptcha}
              placeholder="e.g. nPqxwA"
            />
          </label>
        </div>
        </Spin>
      </Modal>
    </div>
  );
}
